import {
  Button,
  Card,
  Chip,
  Divider,
  Grid,
  useToaster,
  Input,
} from '@enterprise-ui/canvas-ui-react'
import { useEffect, useState } from 'react'
import { useQuery } from '@apollo/client'
import {
  getComponentTypeApi, // gql document
  getGroupTypeDetailApi, // function returning gql document
} from '../../../apiItemGraphql/relationship'
import { isItemhubDomain } from '../../../globalUtils/searchUtills'
import CopyToClipboard from './CopyToClipboard'
import EnterpriseIcon, {
  DuplicateIcon,
  SearchIcon,
  PencilIcon,
} from '@enterprise-ui/icons'
import { useDispatch } from 'react-redux'
import { updateTcinsAndNavigateToMaintain } from '../../../store/updateTcins'
import { useFirefly } from '../../../globalUtils/useAnalytics'
import apiConfig from '../../../globalConfig/apiConfig'
import '../../../globalStyles/relationship.scss'

export const RelationshipComponent = () => {
  const initState = {
    // removed getComponentTypeLists from state; we keep a separate state for master data
    type: 'type', // stores selected groupTypeId (string) or 'type' when none
    typeLabel: '', // stores selected displayValue for convenience
    tcin: '',
    parentTcin: '',
    components: [],
    relationshipTypePayload: null,
    allTcins: [],
  }
  const defaultButtonState = { inProgress: false, disabled: true }

  const dispatch = useDispatch()
  const makeToast = useToaster()
  const { handleTrackCustomEvent } = useFirefly()

  const [state, setState] = useState(initState)
  // 👇 Keep master list as FULL objects (preserve groupTypeId & displayValue)
  // eslint-disable-next-line no-unused-vars
  const [componentTypeListMasterData, setComponentTypeListMasterData] =
    useState([])
  const [buttonState, setButtonState] = useState(defaultButtonState)

  // Load Component Types
  const { data: typeData } = useQuery(getComponentTypeApi, {
    fetchPolicy: 'no-cache',
  })

  // Relationship query (trigger via refetch on button click)
  const relationshipQuery = useQuery(getGroupTypeDetailApi(), {
    variables: { groupTypeId: state.type, tcin: state.tcin },
    fetchPolicy: 'no-cache',
    skip: true,
  })

  // When query returns, cache the FULL objects
  useEffect(() => {
    const raw =
      typeData?.getComponentType?.getComponentTypeLists ??
      typeData?.getComponentType?.componentTypeLists ??
      []
    if (Array.isArray(raw)) setComponentTypeListMasterData(raw)
  }, [typeData])

  // Build dropdown options from the cached FULL objects
  const dropdownOptions = [
    { label: 'Choose Type', value: '' },
    { label: 'Dynamic Assortment', value: '3' },
  ]

  // Enable/disable Find based on both dropdown selection & TCIN length
  useEffect(() => {
    const hasType = state.type !== 'type' && state.type !== ''
    const hasTcin = /^\d{8,}$/.test(String(state.tcin).trim())
    setButtonState((prev) => ({ ...prev, disabled: !(hasType && hasTcin) }))
  }, [state.type, state.tcin])

  // ==== Dynamic Dropdown (native <select>) ====
  const handleTypeChange = (event) => {
    const value = event.target.value // '' or '3'
    if (!value) {
      setState((prev) => ({
        ...prev,
        type: 'type',
        typeLabel: '',
        tcin: '',
        parentTcin: '',
        components: [],
        allTcins: [],
      }))
      return
    }
    const chosen = dropdownOptions.find((o) => o.value === value)
    setState((prev) => ({
      ...prev,
      type: value, // groupTypeId string used for query
      typeLabel: chosen?.label || '', // show this in UI when needed
      parentTcin: '',
      components: [],
      allTcins: [],
    }))
  }

  const handleTcinChange = (event) => {
    const val = event.target.value
    setState((prev) => ({
      ...prev,
      tcin: val,
      ...(val ? {} : { parentTcin: '', components: [], allTcins: [] }),
    }))
  }

  // Button click -> GraphQL refetch
  const findRelationshipCallback = async () => {
    setButtonState((prev) => ({ ...prev, inProgress: true })) // don't force disabled here
    try {
      const { data } = await relationshipQuery.refetch({
        groupTypeId: state.type,
        tcin: state.tcin,
      })

      const payload = data?.getGroupTypeDetail?.groupTypePayload
      const errorMsg = data?.getGroupTypeDetail?.error?.message

      if (!payload) {
        makeToast({
          type: 'error',
          heading: 'Error',
          message: errorMsg || 'No data found.',
        })
        setState((prev) => ({
          ...prev,
          parentTcin: '',
          components: [],
          allTcins: [],
        }))
      } else {
        const parent = payload?.content?.[0]?.parentTcin
        const components = payload?.content?.[0]?.components || []
        const allTcins = [Number(parent), ...components]
        setState((prev) => ({
          ...prev,
          relationshipTypePayload: payload,
          parentTcin: parent,
          components,
          allTcins,
        }))
        handleTrackCustomEvent(
          'DA relationship button click',
          parent,
          components.join?.() || '',
        )
      }
    } catch {
      makeToast({
        type: 'error',
        heading: 'Error',
        message: 'Something went wrong!.',
      })
    } finally {
      setButtonState((prev) => ({ ...prev, inProgress: false }))
    }
  }

  const redirectToSearchScreen = () => {
    const tcins = state.allTcins

    const path = isItemhubDomain ? '/launchpad/search' : '/item-search/base'

    let queryParam = ''
    if (!tcins || tcins.length === 0) {
      queryParam = ''
    } else {
      const values = tcins.map((t) => String(t)).join(',')
      queryParam = `?tcin=${encodeURIComponent(values)}`
    }

    window.location.href = `${window.location.origin}${path}${queryParam}`
  }

  const redirectToMaintainScreen = async () => {
    try {
      const actionResult = await dispatch(
        updateTcinsAndNavigateToMaintain(
          state.allTcins.map((x) => x.toString()),
        ),
      )
      if (
        actionResult.type === updateTcinsAndNavigateToMaintain.fulfilled.type
      ) {
        window.location.href = `${apiConfig.launchpad.host}${apiConfig.launchpad.page.maintain}`
      } else {
        console.error('Failed to update TCINs, action type mismatch')
      }
    } catch (error) {
      console.error('Error while dispatching action:', error)
    }
  }

  // Current selected value for native select ('' for placeholder)
  const selectValue = state.type === 'type' ? '' : state.type

  return (
    <Grid.Container>
      <Grid.Item xs={12}>
        <h1>Dynamic Assortment Lookup</h1>
      </Grid.Item>

      <Grid.Item xs={8}>
        <Card className="hc-pa-3x">
          <Grid.Container>
            {/* 1) Component Type (native select) */}
            <Grid.Item xs={4} className="hc-fs-md da-label-offset">
              Component Type
            </Grid.Item>
            <Grid.Item xs={8} className="hc-mt-dense">
              <select
                id="da-lookup"
                value={selectValue}
                onChange={handleTypeChange}
                className="da-native-select"
              >
                {dropdownOptions.map((opt) => (
                  <option key={opt.value || 'placeholder'} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </Grid.Item>

            {/* 2) TCIN */}
            <Grid.Item
              xs={4}
              style={{ position: 'relative', top: 4 }}
              className="HC-fs-md hc-mt-normal"
            >
              TCIN
            </Grid.Item>
            <Grid.Item xs={3} className="hc-mt-normal">
              <Input.Text
                id="relationship-search"
                placeholder="Enter the TCIN"
                value={state.tcin}
                onChange={handleTcinChange}
                disabled={selectValue === ''} // enable only after user selects a type
              />
            </Grid.Item>
            <Grid.Item xs={5} className="hc-mt-normal">
              <Button
                type="primary"
                disabled={buttonState.disabled}
                onClick={findRelationshipCallback}
                loading={buttonState.inProgress}
              >
                Find Dynamic Assortment Lookup
              </Button>
            </Grid.Item>

            {/* 3) Relationship */}
            <Grid.Item xs={12}>
              <div
                id="relationship-component"
                className="hc-pa-normal da-relationship-box"
              >
                {state.parentTcin ? (
                  <>
                    <Input.Label htmlFor="parent_label">
                      Parent:{' '}
                      <Chip className="da-chip da-chip--highlight">
                        {state.parentTcin}
                      </Chip>
                    </Input.Label>
                    <Divider />
                  </>
                ) : null}
                {state.components?.length ? (
                  <Input.Label
                    htmlFor="component_label"
                    className="hc-pt-dense"
                  >
                    Components:
                  </Input.Label>
                ) : null}
                {state.components?.length ? (
                  <div className="da-chips">
                    {state.components.map((c, i) => (
                      <Chip
                        key={`${c}-${i}`}
                        className={
                          Number(state.tcin) === c
                            ? 'da-chip da-chip--highlight'
                            : 'da-chip'
                        }
                      >
                        {c}
                      </Chip>
                    ))}
                  </div>
                ) : null}
              </div>
            </Grid.Item>

            {/* 4) Actions */}
            <Grid.Item>
              <CopyToClipboard
                TooltipProps={{ title: 'Copied TCINs to clipboard!' }}
              >
                {({ copy }) => (
                  <Button
                    type="secondary"
                    onClick={() => copy(state.allTcins.join())}
                    disabled={!(state.components.length && state.parentTcin)}
                    data-testid="copy-tcins"
                  >
                    <EnterpriseIcon icon={DuplicateIcon} />
                    <span className="hc-pl-dense">Copy TCINs</span>
                  </Button>
                )}
              </CopyToClipboard>
            </Grid.Item>

            <Grid.Item>
              <Button
                type="secondary"
                disabled={!(state.components.length && state.parentTcin)}
                onClick={redirectToMaintainScreen}
                data-testid="redirect-maintain-screen"
              >
                <EnterpriseIcon icon={PencilIcon} />
                <span className="hc-pl-dense">Maintain TCINs</span>
              </Button>
            </Grid.Item>

            <Grid.Item>
              <Button
                type="secondary"
                disabled={!(state.components.length && state.parentTcin)}
                onClick={redirectToSearchScreen}
                data-testid="redirect-search-screen"
              >
                <EnterpriseIcon icon={SearchIcon} />
                <span className="hc-pl-dense">Search TCINs</span>
              </Button>
            </Grid.Item>
          </Grid.Container>
        </Card>
      </Grid.Item>
    </Grid.Container>
  )
}
